import { useEffect, useState } from 'react';
import { Lock, Undo2 } from 'lucide-react';
import { fmt, fmtPct } from '../utils/formatters';

/** Maps backend detector keys to human-friendly eyebrow labels (shared with SampleBriefCard and the home page). */
export const DETECTOR_LABELS: Record<string, string> = {
  promote: 'Rising star',
  decay: 'Fading favorite',
  bundle: 'Bundle opportunity',
  upsell: 'Counter upsell',
  price_creep: 'Cost creep',
  invoice_discrepancy: 'Invoice discrepancy',
  dead_buy: 'Over-ordered',
  margin_leak: 'Margin leak',
  daypart: 'Right item, wrong hours',
  reprice: 'Pricing opportunity',
  drop_rework: 'Menu cleanup',
  anomaly: 'Unusual day',
  staffing: 'Staffing',
  sellout: 'Selling out early',
  reorder: 'Reorder signal',
};

export interface BriefMove {
  rec_id: string;
  detector: string;
  title: string;
  body: string;
  action_label?: string;
  impact_low: number;
  impact_high: number;
  impact_point?: number;
  confidence: number;
  status: 'sent' | 'adopted' | 'dismissed' | 'expired' | string;
  headline_facts?: Record<string, any> | null;
  outcome?: { status?: string; [key: string]: any } | null;
}

export default function BriefCard({ move, index, onRespond, responding }: {
  move: BriefMove;
  index: number;
  onRespond: (recId: string, action: 'adopt' | 'dismiss') => void;
  responding: boolean;
}) {
  const detectorLabel = DETECTOR_LABELS[move.detector] || move.detector;
  const oneTime = Boolean(move.headline_facts?.one_time) || move.detector === 'anomaly';
  const responded = move.status !== 'sent';
  const measured = move.outcome?.status === 'measured';
  const [changing, setChanging] = useState(false);

  // Collapse the change-answer buttons once the new answer lands.
  useEffect(() => {
    setChanging(false);
  }, [move.status]);

  const actionButtons = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onRespond(move.rec_id, 'adopt')}
        disabled={responding}
        className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] px-4 py-2 font-sans text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        I'll do this
      </button>
      <button
        onClick={() => onRespond(move.rec_id, 'dismiss')}
        disabled={responding}
        className="bg-white border border-[#E6E2DE] text-[#6B6670] hover:border-[#4A2D5E] hover:text-[#4A2D5E] rounded-[8px] px-4 py-2 font-sans text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Not for me
      </button>
      {changing && (
        <button
          onClick={() => setChanging(false)}
          disabled={responding}
          className="font-sans text-xs text-[#9B95A0] hover:text-[#6B6670] px-1 transition-colors disabled:opacity-50"
        >
          never mind
        </button>
      )}
    </div>
  );

  /** Responded chip — clickable to change the answer, locked once measured. */
  const respondedChip = () => {
    const isAdopted = move.status === 'adopted';
    const isDismissed = move.status === 'dismissed';
    const label = isAdopted ? 'On it ✓' : isDismissed ? 'Passed' : 'Expired';
    const tone = isAdopted
      ? 'bg-[#2E6B4F]/10 text-[#2E6B4F]'
      : 'bg-[#F2F0ED] text-[#6B6670]';

    if (!isAdopted && !isDismissed) {
      // Expired — plain chip, nothing to change.
      return (
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full font-sans text-sm font-medium ${tone}`}>
          {label}
        </span>
      );
    }

    if (measured) {
      return (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium cursor-default ${tone}`}>
            {label}
            <Lock size={12} strokeWidth={1.5} className="opacity-60" />
          </span>
          <span className="font-sans text-xs text-[#9B95A0]">measured — locked</span>
        </div>
      );
    }

    const hoverTone = isAdopted ? 'hover:bg-[#2E6B4F]/20' : 'hover:bg-[#E6E2DE]';
    return (
      <div className="flex items-center gap-2 group">
        <button
          onClick={() => setChanging(true)}
          disabled={responding}
          aria-label="Change answer"
          title="Change answer"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tone} ${hoverTone}`}
        >
          {label}
          <Undo2 size={13} strokeWidth={1.5} className="opacity-50 group-hover:opacity-100 transition-opacity" />
        </button>
        <span className="font-sans text-xs text-[#9B95A0] opacity-0 group-hover:opacity-100 transition-opacity">
          change answer
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(74,45,94,0.08)] transition-all duration-300">
      <div className="flex items-start gap-4">
        {/* Numbered plum chip */}
        <div className="w-7 h-7 rounded-full bg-[#4A2D5E] text-white flex items-center justify-center font-sans text-sm font-medium flex-shrink-0 mt-0.5">
          {index + 1}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-[#4A2D5E]">
              {detectorLabel}
            </div>
            <span className="text-[11px] font-sans font-medium px-2 py-0.5 rounded-full bg-[#7BA68E]/15 text-[#2E6B4F] whitespace-nowrap">
              {fmtPct(Math.round((move.confidence || 0) * 100))} confident
            </span>
          </div>

          <h3 className="font-serif text-lg text-[#2D2A2E] mt-1">{move.title}</h3>
          <p className="font-sans text-sm text-[#6B6670] mt-1 leading-relaxed">{move.body}</p>

          {/* Impact line */}
          <div className="font-sans text-xl font-semibold text-[#2D2A2E] mt-3 tabular-nums">
            worth {fmt(move.impact_low || 0)}–{fmt(move.impact_high || 0)}{oneTime ? ' one-time' : '/mo'}
            <span className="font-normal text-sm text-[#6B6670]"> — estimated</span>
          </div>

          {/* Actions / state */}
          <div className="mt-4">
            {responded && !changing ? respondedChip() : actionButtons}
          </div>
        </div>
      </div>
    </div>
  );
}
